/*
 * QUEST SOFTWARE PROPRIETARY INFORMATION
 *
 * This software is confidential.  Quest Software Inc., or one of its
 * subsidiaries, has supplied this software to you under terms of a
 * license agreement, nondisclosure agreement or both.
 *
 * You may not copy, disclose, or use this software except in accordance with
 * those terms.
 *
 *
 * Copyright 2026 Quest Software Inc.
 * ALL RIGHTS RESERVED.
 *
 * QUEST SOFTWARE INC. MAKES NO REPRESENTATIONS OR
 * WARRANTIES ABOUT THE SUITABILITY OF THE SOFTWARE,
 * EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED
 * TO THE IMPLIED WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE, OR
 * NON-INFRINGEMENT.  QUEST SOFTWARE SHALL NOT BE
 * LIABLE FOR ANY DAMAGES SUFFERED BY LICENSEE
 * AS A RESULT OF USING, MODIFYING OR DISTRIBUTING
 * THIS SOFTWARE OR ITS DERIVATIVES.
 */

import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  isCloudFlagSelector,
  isCloudUser,
  isSaasAcceptedSelector,
} from '../store/app.selectors';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { skipEmpty } from '../shared/utils/skip-empty.operator';
import { isEmpty } from 'lodash-es';
import {
  FacQibAuthService,
  FacAuthService,
  QibSigninService,
  AppInfoService,
} from '@foglight/angular-common';
import { UtilService } from './util.service';

@Injectable()
export class LayoutService implements OnDestroy {
  private destroy$ = new Subject<void>();
  private _showSidebar = new BehaviorSubject<boolean>(false);
  private isCloud: boolean;
  public showSideBar = this._showSidebar.asObservable();

  constructor(
    private store: Store,
    private qibSigninService: QibSigninService,
    private qibAuthService: FacQibAuthService,
    private appInfoService: AppInfoService,
    private authService: FacAuthService,
    private utilService: UtilService
  ) {}

  public verifyCloudAndSaasAcceptance() {
    this.store
      .select(isCloudFlagSelector)
      .pipe(takeUntil(this.destroy$))
      .subscribe((isCloud) => {
        this.isCloud = isCloud;
        if (isCloud) {
          this.store
            .select(isCloudUser)
            .pipe(takeUntil(this.destroy$))
            .subscribe((cloudUser) => {
              if (cloudUser) {
                this.store
                  .select(isSaasAcceptedSelector)
                  .pipe(takeUntil(this.destroy$), skipEmpty())
                  .subscribe((saasAccepted) => {
                    this._showSidebar.next(saasAccepted);
                  });
              } else {
                this._showSidebar.next(true);
              }
            });
        } else {
          this._showSidebar.next(true);
        }
      });
  }

  logOut(): void {
    if (!this.isCloud && !isEmpty(this.qibAuthService.qibId_token)) {
      this.qibSigninService.onQibSignOut();
    } else {
      this.qibAuthService.resetQibAuthTokens();
      this.appInfoService.resetAppInfo();
      this.authService.logout();
    }

    this.utilService.clearSessionOnLogOut();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
